// useHackathon — central data hook for the hackathon feature.
//
// Returns the live pot info (computed server-side from `subscribers`),
// the public scoreboard, monthly + annual winners, and a join/leave
// helper for the signed-in user.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type HackathonPot = {
  paid_users: number;
  unlock_threshold_users: number;
  pot_cents: number;
  seed_amount_cents: number;
  per_user_before_cents: number;
  per_user_after_cents: number;
  unlocked: boolean;
  active: boolean;
};

export type ScoreboardRow = {
  user_id: string;
  handle: string;
  avatar_url: string | null;
  total_xp: number;
  monthly_xp: number;
  published_artifacts: number;
  projects_built: number;
  score: number;
  score_monthly: number;
  joined_hackathon: boolean;
};

export type MonthlyWinner = {
  id: string;
  year: number;
  month: number;
  user_id: string;
  score_snapshot: number;
  prize_cents: number;
  paid_at: string | null;
};

export type AnnualWinner = {
  id: string;
  year: number;
  user_id: string;
  title: string;
  score_snapshot: number;
  prize_cents: number;
};

export function formatUsd(cents: number): string {
  const dollars = Math.round(cents / 100);
  return "$" + dollars.toLocaleString();
}

export function useHackathon(opts?: { scoreboardLimit?: number }) {
  const { user } = useAuth();
  const limit = opts?.scoreboardLimit ?? 25;

  const [pot, setPot] = useState<HackathonPot | null>(null);
  const [scoreboard, setScoreboard] = useState<ScoreboardRow[]>([]);
  const [monthly, setMonthly] = useState<MonthlyWinner[]>([]);
  const [annual, setAnnual] = useState<AnnualWinner[]>([]);
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [potRes, boardRes, monthRes, yearRes] = await Promise.all([
      supabase.rpc("get_hackathon_pot"),
      supabase
        .from("hackathon_scoreboard" as any)
        .select("*")
        .order("score", { ascending: false })
        .limit(limit),
      supabase
        .from("hackathon_monthly_winners")
        .select("*")
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .limit(12),
      supabase
        .from("hackathon_annual_winners")
        .select("*")
        .order("year", { ascending: false })
        .limit(5),
    ]);

    if (potRes.data) setPot(potRes.data as unknown as HackathonPot);
    if (boardRes.data) setScoreboard(boardRes.data as unknown as ScoreboardRow[]);
    if (monthRes.data) setMonthly(monthRes.data as unknown as MonthlyWinner[]);
    if (yearRes.data) setAnnual(yearRes.data as unknown as AnnualWinner[]);

    if (user) {
      const { data } = await supabase
        .from("hackathon_participants")
        .select("user_id")
        .eq("user_id", user.id)
        .maybeSingle();
      setJoined(!!data);
    } else {
      setJoined(false);
    }

    setLoading(false);
  }, [user, limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const join = useCallback(
    async (pitch?: string) => {
      if (!user) return { error: "not_signed_in" as const };
      const { error } = await supabase
        .from("hackathon_participants")
        .upsert({ user_id: user.id, pitch: pitch ?? null });
      if (!error) {
        setJoined(true);
        refresh();
      }
      return { error: error?.message ?? null };
    },
    [user, refresh]
  );

  const leave = useCallback(async () => {
    if (!user) return;
    await supabase.from("hackathon_participants").delete().eq("user_id", user.id);
    setJoined(false);
    refresh();
  }, [user, refresh]);

  return { pot, scoreboard, monthly, annual, joined, loading, join, leave, refresh };
}